import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';

// Visual In/Out picker for a Patreon public preview — reports start_s + duration_s.
//
// AUDIO → drag/resize a region over a decoded waveform (like the midroll editor).
// VIDEO → a <video> scrubber with "Set In / Set Out" buttons. WaveSurfer decodes
//   audio via the Web Audio API's decodeAudioData(), which CANNOT decode a video
//   container (mp4/mov) — it fails with "Could not decode this audio file". So for
//   video we never touch WaveSurfer; we scrub the <video> element directly.

interface Props {
  file?: File | null;
  url?: string | null;   // alternative source (edit mode: already-uploaded audio)
  startS: number;
  durationS: number;
  onChange: (startS: number, durationS: number) => void;
}

const PREVIEW_COLOR = 'rgba(150,76,94,0.35)';

function fmt(sec: number): string {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ── VIDEO: scrub the <video> element, mark In/Out from the playhead ──────────
const VideoPreviewPicker: React.FC<Props> = ({ file, url, startS, durationS, onChange }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const objUrlRef = useRef<string | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [src, setSrc] = useState<string>('');
  const [cur, setCur] = useState(0);
  const [start, setStart] = useState(startS);
  const [end, setEnd] = useState(startS + durationS);

  useEffect(() => {
    if (!file && !url) return;
    const s = file ? URL.createObjectURL(file) : (url as string);
    objUrlRef.current = file ? s : null;
    setSrc(s);
    return () => {
      if (objUrlRef.current) { URL.revokeObjectURL(objUrlRef.current); objUrlRef.current = null; }
    };
  }, [file, url]);

  const emit = (a: number, b: number) => {
    const st = Math.max(0, Math.min(a, Math.max(0, b - 1)));
    const en = Math.max(st + 1, b);
    setStart(st); setEnd(en);
    onChangeRef.current(Math.round(st), Math.round(en - st));
  };

  return (
    <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Public preview — scrub the video, then mark the In / Out points patrons see for free.
      </Typography>
      {src && (
        <video
          ref={videoRef}
          src={src}
          controls
          preload="metadata"
          style={{ width: '100%', maxHeight: 300, background: '#000', borderRadius: 4 }}
          onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)}
        />
      )}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mt: 1, gap: 1 }}>
        <Button size="small" variant="outlined"
          onClick={() => emit(videoRef.current?.currentTime ?? 0, end)}>
          Set In (@ {fmt(cur)})
        </Button>
        <Button size="small" variant="outlined"
          onClick={() => emit(start, videoRef.current?.currentTime ?? 0)}>
          Set Out (@ {fmt(cur)})
        </Button>
        <Typography variant="caption">
          In <strong>{fmt(start)}</strong> · Out <strong>{fmt(end)}</strong> ·
          length <strong>{fmt(end - start)}</strong>
        </Typography>
      </Stack>
    </Box>
  );
};

// ── AUDIO: WaveSurfer waveform + draggable region ────────────────────────────
const AudioWavePreviewPicker: React.FC<Props> = ({ file, url, startS, durationS, onChange }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const regionsRef = useRef<any>(null);
  const regionRef = useRef<any>(null);
  const objUrlRef = useRef<string | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [win, setWin] = useState<{ start: number; end: number }>(
    { start: startS, end: startS + durationS });
  const winRef = useRef(win);
  winRef.current = win;

  useEffect(() => {
    if ((!file && !url) || !containerRef.current) return;
    let cancelled = false;
    setReady(false);
    setError(null);
    // local file → object URL; otherwise load the provided (presigned) URL
    const srcUrl = file ? URL.createObjectURL(file) : (url as string);
    objUrlRef.current = file ? srcUrl : null;

    const regions = RegionsPlugin.create();
    regionsRef.current = regions;
    const ws = WaveSurfer.create({
      container: containerRef.current,
      height: 96,
      waveColor: '#b9a3ab',
      progressColor: '#964c5e',
      cursorColor: '#222',
      url: srcUrl,
      plugins: [regions],
    });
    wsRef.current = ws;

    ws.on('ready', () => {
      if (cancelled) return;
      const dur = ws.getDuration();
      const start = Math.min(startS, Math.max(0, dur - 1));
      const end = Math.min(start + durationS, dur);
      const region = regions.addRegion({
        start, end, color: PREVIEW_COLOR, drag: true, resize: true,
      });
      regionRef.current = region;
      setWin({ start, end });
      setReady(true);
    });
    regions.on('region-updated', (region: any) => {
      const start = Math.max(0, region.start);
      const end = Math.max(start + 1, region.end);
      setWin({ start, end });
      onChangeRef.current(Math.round(start), Math.round(end - start));
    });
    // track the playhead + stop at the Out point (region-out was unreliable)
    ws.on('timeupdate', (t: number) => {
      setCurrent(t);
      if (ws.isPlaying() && t >= winRef.current.end) {
        try { ws.pause(); } catch { /* noop */ }
      }
    });
    ws.on('play', () => setPlaying(true));
    ws.on('pause', () => setPlaying(false));
    ws.on('finish', () => setPlaying(false));
    ws.on('error', () => { if (!cancelled) setError('Could not decode this audio file.'); });

    return () => {
      cancelled = true;
      try { wsRef.current?.destroy(); } catch { /* noop */ }
      wsRef.current = null; regionsRef.current = null; regionRef.current = null;
      if (objUrlRef.current) { URL.revokeObjectURL(objUrlRef.current); objUrlRef.current = null; }
    };
    // re-init only when the source changes (start/duration seed the initial region)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, url]);

  const playPreview = () => {
    const ws = wsRef.current, r = regionRef.current;
    if (!ws || !r) return;
    if (ws.isPlaying()) { ws.pause(); return; }
    // seek to In and play (reliable; region.play() needed several clicks)
    try { ws.setTime(r.start); } catch { /* noop */ }
    Promise.resolve(ws.play()).catch(() => { /* autoplay/interrupt */ });
  };

  return (
    <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Public preview — drag/resize the highlighted window to set the In/Out points patrons hear.
      </Typography>
      <div ref={containerRef} />
      {error && <Typography variant="caption" color="error">{error}</Typography>}
      {ready && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Button size="small" variant="outlined" onClick={playPreview}
            startIcon={playing ? <PauseIcon /> : <PlayArrowIcon />}>
            {playing ? 'Pause' : 'Preview'}
          </Button>
          <Typography variant="caption">
            In <strong>{fmt(win.start)}</strong> · Out <strong>{fmt(win.end)}</strong> ·
            length <strong>{fmt(win.end - win.start)}</strong>
            {playing && <> · ▶ <strong>{fmt(current)}</strong></>}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

// A video container (mp4/mov/webm) can't be decoded by the Web Audio API, so
// route those to the <video> scrubber; everything else uses the waveform.
function isVideoSource(file?: File | null, url?: string | null): boolean {
  if (file) return (file.type || '').startsWith('video/') || /\.(mp4|mov|m4v|webm)$/i.test(file.name || '');
  if (url) return /\.(mp4|mov|m4v|webm)(\?|$)/i.test(url);
  return false;
}

const PatreonPreviewPicker: React.FC<Props> = (props) => {
  if (!props.file && !props.url) return null;
  return isVideoSource(props.file, props.url)
    ? <VideoPreviewPicker {...props} />
    : <AudioWavePreviewPicker {...props} />;
};

export default PatreonPreviewPicker;
